'use client';

import * as React from 'react';
import { Building2, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import SquaredDotIcon from './Icons/SquaredDotIcon';
import Button from './Button';
import { useParams, usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { STOREFRONT } from '@/lib/utils/translation';

interface Category {
  id: string;
  label: string;
  group?: string;
}

interface CityComboboxProps {
  categories: Category[];
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onSelect?: (categoryId: string) => void;
  isLoading?: boolean;
}

export default function CityCombobox({
  categories,
  searchQuery,
  onSearchChange,
  onSelect,
  isLoading,
}: CityComboboxProps) {
  const pathname = usePathname();
  const params = useParams();
  const [open, setOpen] = React.useState(false);
  const [selectedValue, setSelectedValue] = React.useState<string>('');
  const selectedItem = React.useRef<Category | null>(null);

  const t = useTranslations();

  React.useEffect(() => {
    if (!pathname.includes('/city')) {
      setSelectedValue('');
      selectedItem.current = null;
    } else if (params?.slug) {
      setSelectedValue(params.slug as string);
    }
    return () => {
      // Cleanup if needed
      setSelectedValue('');
      selectedItem.current = null;
    };
  }, [params?.slug, pathname]);

  const handleSelect = (value: string) => {
    const newValue = value === selectedValue ? '' : value;
    const selectedCategory = categories.find((cat) => cat.id === newValue);
    selectedItem.current = selectedCategory ?? null;
    setSelectedValue(newValue);
    onSelect?.(newValue);
    setOpen(false);
  };

  let selectedLabel: string | null = null;

  if (selectedItem?.current) {
    selectedLabel = selectedItem.current?.label ?? null;
  } else {
    const queryCategory = categories.find((cat) => cat.id === params?.slug);
    if (queryCategory) {
      selectedLabel = queryCategory.label;
    } else if (selectedValue) {
      selectedLabel =
        selectedValue.charAt(0).toUpperCase() + selectedValue.slice(1);
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          transparent
          role="combobox"
          aria-expanded={open}
          className="flex w-full min-w-[120px] items-center justify-between space-x-2 px-3 py-2 text-left lg:justify-start"
        >
          <div className="flex items-center space-x-2">
            <span>
              <Building2 className="" color="#A5A5AA" />
            </span>
            <span>
              {selectedLabel ?? t(STOREFRONT.HOME + '.hero_section.my_city')}
            </span>
          </div>
          <div className="text-primary-800">
            {open ? <ChevronUp /> : <ChevronDown />}
          </div>
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className="w-[250px] rounded-md bg-white px-3 py-4"
        align="start"
      >
        <Command className="rounded-lg border-0">
          <CommandInput
            onValueChange={onSearchChange}
            placeholder={t('common.search_city')}
            className="h-8"
            value={searchQuery}
            onClear={
              searchQuery?.length > 0
                ? () => {
                    // setSelectedValue('');
                    onSearchChange('');
                  }
                : undefined
            }
          />
          <CommandList>
            {categories.length === 0 && !isLoading && (
              <CommandEmpty>{t('common.nothing_found')}</CommandEmpty>
            )}
            {categories.length > 0 && (
              <CommandGroup className={cn(isLoading && 'opacity-60')}>
                {categories.map((category) => (
                  <CommandItem
                    key={category.id}
                    value={category.id}
                    onSelect={handleSelect}
                    className={cn(
                      'mx-2 my-1 cursor-pointer rounded-lg py-2 hover:bg-primary-800 hover:text-white',
                      // 'data-[selected=true]:bg-primary-800 data-[selected=true]:text-white',
                      selectedValue === category.id &&
                        'bg-primary-800 text-white'
                    )}
                  >
                    {category.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {isLoading && categories.length > 0 && (
              <div className="bg-background/50 absolute inset-0 flex items-center justify-center rounded-lg backdrop-blur-sm">
                {t('common.loading')}
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
